import { BackgroundCheckStatus } from '@/backend/features/background-check/types';
import { getAllBackgroundCheckStatuses } from '@/ui/features/background-check/utils';
import { FilterConfigBuilder } from '@juvare/common-data-table-ui/FiltersBar/utils';
import { WidgetFormPanelConfig } from '@juvare/common-form-ui/WidgetFormPanel';
import { AccountFilterData } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountList';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export interface ExtendRmsFiltersParams {
    addPrimaryBackgroundCheckStatusQuickFilter?: boolean;
}

export function useExtendedRmsAccountFilters() {
    const { t } = useTranslation('rms');
    const { backgroundCheckConfig } = useSelector(getSettings);
    const statuses: BackgroundCheckStatus[] = getAllBackgroundCheckStatuses(backgroundCheckConfig.type);
    const userState = useUserState();
    const featureFlags = userState?.credentialing?.featureFlags ?? [];
    const extendRmsFilters = (builder: FilterConfigBuilder<AccountFilterData>, params: ExtendRmsFiltersParams = {}) => {
        if (featureFlags.includes('tribe')) {
            const locationPanel = builder.findAllFiltersWidget('!location') as WidgetFormPanelConfig;
            locationPanel.children.push(
                builder.allFiltersWidget({
                    label: t('accountsFilters.location.stateOfWorksLabel'),
                    type: 'form.select',
                    key: 'stateOfWork',
                    valueProvider: {
                        type: 'classifier.state',
                    },
                }),
            );
        }
        const primaryBackgroundCheckTypeWidget = builder.allFiltersWidget({
            type: 'form.select',
            label: t('accountsFilters.backgroundCheck.type'),
            key: 'primaryBackgroundCheckType',
            valueProvider: {
                type: 'backgroundCheckEnabledTypes',
            },
        });
        const primaryBackgroundCheckStatusOptions = statuses.map((value) => ({
            value: value,
            label: t(`backgroundCheck.statuses.${value}`),
        }));
        const primaryBackgroundCheckStatusWidget = params.addPrimaryBackgroundCheckStatusQuickFilter
            ? builder.quickFilterMultiSelect({
                  dataPath: 'primaryBackgroundCheckStatus',
                  label: t('accountsFilters.backgroundCheck.status'),
                  widget: 'form.multiSelect',
                  values: primaryBackgroundCheckStatusOptions,
                  sx: { maxWidth: { xs: 1, sm: 280 } },
              })
            : builder.allFiltersWidget({
                  type: 'form.multiSelect',
                  label: t('accountsFilters.backgroundCheck.status'),
                  key: 'primaryBackgroundCheckStatus',
                  values: primaryBackgroundCheckStatusOptions,
              });
        builder.allFiltersPanel({
            key: '!backgroundCheck',
            label: t('accountsFilters.backgroundCheck.title'),
            children: [primaryBackgroundCheckTypeWidget, primaryBackgroundCheckStatusWidget],
        });
        builder.allFiltersPanel({
            key: '!esarVhp',
            label: t('accountsFilters.esarVhp.title'),
            children: [
                builder.allFiltersWidget({
                    key: 'esarVhpId',
                    label: '',
                    type: 'form.text',
                }),
            ],
        });
    };
    return { extendRmsFilters };
}
